import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LogOut, CloudUpload, Hash, History, Lock, Trash2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

function todayISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  return (
    <>
      <PageHeader
        title="Admin Dashboard"
        subtitle={user?.email}
        right={
          <Button
            data-testid={ADMIN.logoutBtn}
            variant="outline"
            className="rounded-full"
            onClick={logout}
          >
            <LogOut className="h-4 w-4 mr-1.5" /> Logout
          </Button>
        }
      />
      <div className="max-w-xl mx-auto w-full px-4 pb-10">
        <Tabs defaultValue="result" className="w-full">
          <TabsList className="w-full grid grid-cols-3 h-auto bg-slate-100 p-1 rounded-2xl gap-1">
            <TabsTrigger value="result" data-testid={ADMIN.tabDaily} className="rounded-xl text-xs">
              <CloudUpload className="h-3.5 w-3.5 mr-1" /> Result
            </TabsTrigger>
            <TabsTrigger value="previous" data-testid="admin-tab-previous" className="rounded-xl text-xs">
              <History className="h-3.5 w-3.5 mr-1" /> Previous
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="admin-tab-password" className="rounded-xl text-xs">
              <Lock className="h-3.5 w-3.5 mr-1" /> Password
            </TabsTrigger>
          </TabsList>

          <TabsContent value="result" className="mt-4"><ResultForm /></TabsContent>
          <TabsContent value="previous" className="mt-4"><PreviousAdmin /></TabsContent>
          <TabsContent value="password" className="mt-4"><PasswordForm /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function Alert({ tone = "sky", children }) {
  const tones = {
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return <div className={`rounded-xl border ${tones[tone]} text-xs px-3 py-2 mb-4`}>{children}</div>;
}

function ResultForm() {
  const [form, setForm] = useState({ date: todayISO(), r1: "", r2: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/results", form);
      toast.success("Result published");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200">
      <Alert tone="sky">Publish today's or any date's First & Second round result. You can also add an optional note that appears with the result.</Alert>
      <div className="space-y-4">
        <div>
          <Label>Date</Label>
          <Input data-testid={ADMIN.dailyDate} type="date" value={form.date} onChange={set("date")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First Round (F/R)</Label>
            <Input data-testid={ADMIN.dailyR1} value={form.r1} onChange={set("r1")} maxLength={2} placeholder="00" className="font-numbers text-xl text-center" />
          </div>
          <div>
            <Label>Second Round (S/R)</Label>
            <Input data-testid={ADMIN.dailyR2} value={form.r2} onChange={set("r2")} maxLength={2} placeholder="00" className="font-numbers text-xl text-center" />
          </div>
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Textarea rows={2} data-testid="admin-daily-note" value={form.note} onChange={set("note")} placeholder="Any announcement for the day…" />
        </div>
        <Button data-testid={ADMIN.dailySave} onClick={save} disabled={saving} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
          <CloudUpload className="h-4 w-4 mr-2" /> {saving ? "Publishing…" : "Publish Result"}
        </Button>
      </div>
    </Card>
  );
}

function CommonForm() {
  const [f, setF] = useState({
    date: todayISO(),
    r1_direct: "", r1_house: "", r1_ending: "",
    r2_direct: "", r2_house: "", r2_ending: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/common-numbers", f);
      toast.success("Common numbers published");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200">
      <Alert tone="violet">Publish Direct (comma-separated), House and Ending picks for First and Second Round. Add an optional note for study context.</Alert>
      <div className="space-y-4">
        <div>
          <Label>Date</Label>
          <Input data-testid={ADMIN.commonDate} type="date" value={f.date} onChange={set("date")} />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-600">First Round</span>
          </div>
          <Input data-testid={ADMIN.comR1Dir} placeholder="Direct e.g. 12,34,56" value={f.r1_direct} onChange={set("r1_direct")} className="mb-2" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid={ADMIN.comR1Hou} placeholder="House" value={f.r1_house} onChange={set("r1_house")} />
            <Input data-testid={ADMIN.comR1End} placeholder="Ending" value={f.r1_ending} onChange={set("r1_ending")} />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-full bg-indigo-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-600">Second Round</span>
          </div>
          <Input data-testid={ADMIN.comR2Dir} placeholder="Direct e.g. 21,43,65" value={f.r2_direct} onChange={set("r2_direct")} className="mb-2" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid={ADMIN.comR2Hou} placeholder="House" value={f.r2_house} onChange={set("r2_house")} />
            <Input data-testid={ADMIN.comR2End} placeholder="Ending" value={f.r2_ending} onChange={set("r2_ending")} />
          </div>
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Textarea rows={2} data-testid="admin-common-note" value={f.note} onChange={set("note")} placeholder="Study context or analyst comment…" />
        </div>
        <Button data-testid={ADMIN.commonSave} onClick={save} disabled={saving} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
          {saving ? "Publishing…" : "Publish Common Numbers"}
        </Button>
      </div>
    </Card>
  );
}

function fmtDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function PreviousAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [draft, setDraft] = useState({ r1: "", r2: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/results", { params: { limit: 200 } })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (it) => {
    setEditingDate(it.date);
    setDraft({ r1: it.r1 || "", r2: it.r2 || "", note: it.note || "" });
  };

  const cancelEdit = () => {
    setEditingDate(null);
    setDraft({ r1: "", r2: "", note: "" });
  };

  const save = async () => {
    if (!editingDate) return;
    setSaving(true);
    try {
      await api.post("/results", { date: editingDate, ...draft });
      toast.success(`Updated ${fmtDMY(editingDate)}`);
      cancelEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (date) => {
    if (!window.confirm(`Delete result for ${fmtDMY(date)}?`)) return;
    try {
      await api.delete(`/results/${date}`);
      toast.success("Deleted");
      if (editingDate === date) cancelEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Card className="p-5 border-slate-200">
      <Alert tone="sky">List of all previously published results. Tap Edit to modify a row inline, or the bin to delete.</Alert>
      {loading ? (
        <div className="text-center text-slate-500 py-4">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm text-center py-4">No results published yet.</div>
      ) : (
        <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
          {items.map((it) => {
            const isEditing = editingDate === it.date;
            return (
              <div
                key={it.id}
                data-testid={`admin-prev-row-${it.date}`}
                className={`rounded-xl border px-3 py-2.5 ${
                  isEditing ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"
                }`}
              >
                {!isEditing ? (
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold text-slate-700 w-24">{fmtDMY(it.date)}</div>
                    <div className="font-numbers font-bold text-lg text-slate-900 w-10 text-center">
                      {it.r1 ? String(it.r1).padStart(2, "0") : "--"}
                    </div>
                    <div className="font-numbers font-bold text-lg text-slate-900 w-10 text-center">
                      {it.r2 ? String(it.r2).padStart(2, "0") : "--"}
                    </div>
                    <div className="flex-1 text-xs text-slate-500 truncate">{it.note || ""}</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(it)}
                      data-testid={`admin-prev-edit-${it.date}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => del(it.date)}
                      data-testid={`admin-prev-delete-${it.date}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-heading font-bold text-slate-900">
                        Editing {fmtDMY(it.date)}
                      </span>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        Cancel
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">F/R</Label>
                        <Input
                          value={draft.r1}
                          maxLength={2}
                          onChange={(e) => setDraft({ ...draft, r1: e.target.value })}
                          className="font-numbers text-lg text-center"
                          data-testid={`admin-prev-r1-${it.date}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">S/R</Label>
                        <Input
                          value={draft.r2}
                          maxLength={2}
                          onChange={(e) => setDraft({ ...draft, r2: e.target.value })}
                          className="font-numbers text-lg text-center"
                          data-testid={`admin-prev-r2-${it.date}`}
                        />
                      </div>
                    </div>
                    <Textarea
                      rows={2}
                      value={draft.note}
                      onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                      placeholder="Optional note"
                      data-testid={`admin-prev-note-${it.date}`}
                    />
                    <Button
                      onClick={save}
                      disabled={saving}
                      className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`admin-prev-save-${it.date}`}
                    >
                      {saving ? "Saving…" : "Save Changes"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function DreamAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ keyword: "", number: "" });

  const load = () => api.get("/dream", { params: { q: "" } }).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/dream", f);
      toast.success("Saved");
      setF({ keyword: "", number: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this dream entry?")) return;
    await api.delete(`/dream/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200">
        <Alert tone="violet">Add or update a dream → number mapping. Entries appear on the public Dream Number page in colored boxes.</Alert>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid={ADMIN.dreamKeyword} placeholder="Keyword (e.g. water, fish)" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} />
          <Input data-testid={ADMIN.dreamNumber} placeholder="Number (e.g. 23)" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
        </div>
        <Button data-testid={ADMIN.dreamSave} onClick={save} className="w-full rounded-full mt-3 bg-emerald-600 hover:bg-emerald-700">
          Add / Update
        </Button>
      </Card>
      <Card className="p-4 border-slate-200">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">All dream numbers ({items.length})</div>
        <div className="grid grid-cols-2 gap-2 max-h-[440px] overflow-y-auto pr-1">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-semibold capitalize">{it.keyword}</div>
                <div className="font-numbers font-bold text-lg">{it.number}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => del(it.id)}>
                <Trash2 className="h-4 w-4 text-rose-600" />
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function PasswordForm() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const { logout } = useAuth();

  const save = async () => {
    if (f.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (f.new_password !== f.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: f.current_password,
        new_password: f.new_password,
      });
      toast.success("Password changed — please log in again");
      setTimeout(() => logout(), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200">
      <Alert tone="rose">Change your admin password. You'll be logged out and asked to sign in with the new password.</Alert>
      <div className="space-y-3">
        <div>
          <Label>Current password</Label>
          <Input data-testid="admin-pass-current" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
        </div>
        <div>
          <Label>New password</Label>
          <Input data-testid="admin-pass-new" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input data-testid="admin-pass-confirm" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
        </div>
        <Button data-testid="admin-pass-save" onClick={save} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
          {saving ? "Updating…" : "Change Password"}
        </Button>
      </div>
    </Card>
  );
}
